import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CompanyReadModel,
  SpaceDiscriminator,
  useGetBuildingsByIdInvestorQuery,
  useGetBuildingsByIdManagerQuery,
  useGetBuildingsByIdOwnerQuery,
  usePutSpacesByIdInvestorMutation,
  usePutSpacesByIdManagerMutation,
  usePutSpacesByIdOwnerMutation,
} from '@client/api';
import { PageDetailContent } from '@client/toolkit';

import { PageContentAnimation } from '../../../../../components';
import { CompaniesCard } from '../../../components/companies-card/companies-card.component';

export function SpaceCompaniesRolesRoute() {
  const { spaceId } = useParams<{
    spaceId: string;
    discriminator: SpaceDiscriminator;
  }>();
  const { t: tType } = useTranslation('type');
  const { t: tNotification } = useTranslation('notification');

  const {
    data: owners,
    isFetching: isFetchingOwners,
    refetch: refetchOwners,
  } = useGetBuildingsByIdOwnerQuery({ id: spaceId ?? '' }, { skip: !spaceId });

  const {
    data: investors,
    isFetching: isFetchingInvestors,
    refetch: refetchInvestors,
  } = useGetBuildingsByIdInvestorQuery({ id: spaceId ?? '' }, { skip: !spaceId });

  const {
    data: manager,
    isFetching: isFetchingManager,
    refetch: refetchManager,
  } = useGetBuildingsByIdManagerQuery({ id: spaceId ?? '' }, { skip: !spaceId });

  const [putOwners] = usePutSpacesByIdOwnerMutation();
  const [putInvestors] = usePutSpacesByIdInvestorMutation();
  const [putManagers] = usePutSpacesByIdManagerMutation();

  const handleEditOwners = (data: CompanyReadModel[]) => {
    putOwners({ id: spaceId ?? '', body: { owners: data.map((c) => c.id) } })
      .unwrap()
      .then(() => {
        refetchOwners();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  const handleEditInvestors = (data: CompanyReadModel[]) => {
    putInvestors({ id: spaceId ?? '', body: { investors: data.map((c) => c.id) } })
      .unwrap()
      .then(() => {
        refetchInvestors();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  const handleEditManager = (data: CompanyReadModel[]) => {
    putManagers({ id: spaceId ?? '', body: { tenantId: data[0]?.id ?? '' } })
      .unwrap()
      .then(() => {
        refetchManager();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  return (
    <PageDetailContent align="left">
      <PageContentAnimation className="flex flex-col gap-6">
        <CompaniesCard
          role="Owner"
          spaceId={spaceId}
          title={tType('RelationshipType.Owner', { context: 'plural' })}
          isLoading={isFetchingOwners}
          companies={owners ?? []}
          onEdit={handleEditOwners}
        />
        <CompaniesCard
          role="Investor"
          spaceId={spaceId}
          title={tType('RelationshipType.Investor', { context: 'plural' })}
          isLoading={isFetchingInvestors}
          companies={investors ?? []}
          onEdit={handleEditInvestors}
        />
        <CompaniesCard
          role="Manager"
          spaceId={spaceId}
          title={tType('RelationshipType.Manager', { context: 'plural' })}
          isLoading={isFetchingManager}
          companies={manager ? [manager] : []}
          onEdit={handleEditManager}
        />
      </PageContentAnimation>
    </PageDetailContent>
  );
}
